import { v4 as uuidv4 } from 'uuid';
import {
  MulesoftOffering,
  MulesoftOfferingRef,
} from '../models/mulesoft-offering';
import { Offering } from '../models/offering';
import { OfferingType } from '../enums/offering-type.enum';
import { SERVICES_SECTION } from '../constants/offering.constants';

/**
 * Maps the Mulesoft Process-API offering shape into the view models the app
 * already consumes:
 *  - `mapMulesoftToOfferingSummary` → the flat summary used by the offering
 *    signals (public list, filters, focus "my offerings").
 *  - `mapMulesoftOfferingToComponents` → the dynamic `componentsArray` rendered
 *    by the public offering detail page (mirrors `mapStoryblokOfferingToComponents`).
 *
 * Relation fields (`successor`, `similarOfferings`, `relatedTechnology.targetId`)
 * are resolved against the loaded offering set into the Storyblok-resolved story
 * shape (`{ slug, content: { displayName, offeringImage, description } }`) so the
 * existing display components keep working unchanged.
 */

function stripNullish<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== null && v !== undefined),
  ) as Partial<T>;
}

/** Flat summary used by the offering list/filter views. */
export function mapMulesoftToOfferingSummary(o: MulesoftOffering): Offering {
  return {
    id: o.id,
    uuid: o.uuid,
    slug: o.slug ?? '',
    full_slug: o.fullSlug ?? '',
    publishedAt: o.publishedAt ?? '',
    updatedAt: o.updatedAt ?? '',
    displayName: o.displayName ?? '',
    offeringImage: o.offeringImage ?? '',
    description: o.description ?? '',
    categories: o.categories ?? [],
    offeringType: o.offeringType ?? '',
    approvalStatus: o.approvalStatus ?? '',
    knowledge: o.knowledge ?? '',
    classificationLevel: o.classification?.classificationLevel ?? '',
    ownerEmail: o.contentOwnership?.ownerEmail ?? '',
    deputyEmail: o.contentOwnership?.deputyEmail ?? '',
    authorEmails: o.contentOwnership?.authorEmails ?? [],
  };
}

/** A resolved relation shaped like a Storyblok resolved story. */
interface ResolvedOfferingRef {
  slug?: string;
  content: {
    displayName?: string;
    offeringImage?: string;
    description?: string;
  };
}

/** Build an id/uuid → offering index for client-side relation resolution. */
export function buildOfferingIndex(
  offerings: MulesoftOffering[],
): Map<string, MulesoftOffering> {
  const index = new Map<string, MulesoftOffering>();
  for (const offering of offerings) {
    if (offering.id != null) {
      index.set(String(offering.id), offering);
    }
    if (offering.uuid) {
      index.set(offering.uuid, offering);
    }
  }
  return index;
}

function toResolvedRef(offering: MulesoftOffering): ResolvedOfferingRef {
  return {
    slug: offering.slug,
    content: {
      displayName: offering.displayName,
      offeringImage: offering.offeringImage,
      description: offering.description,
    },
  };
}

/**
 * Resolve an offering reference (id/uuid) to a Storyblok-resolved story shape.
 * Free-text labels that don't match a known offering are returned unchanged,
 * mirroring Storyblok's behaviour for unresolved relations.
 */
function resolveRef(
  ref: MulesoftOfferingRef,
  index: Map<string, MulesoftOffering>,
): ResolvedOfferingRef | string {
  if (ref == null) {
    return ref;
  }
  const found = index.get(String(ref));
  return found ? toResolvedRef(found) : ref;
}

/** Normalize SSO providers (string or array) into a string array for display. */
function normalizeProviders(
  providers: string[] | string | null | undefined,
): string[] {
  if (providers == null) {
    return [];
  }
  return Array.isArray(providers) ? providers : [providers];
}

function mapHosting(o: MulesoftOffering) {
  return (o.hosting ?? []).map((h) => ({
    name: h.hostingName ?? '',
    type: h.hostingType ?? '',
    managed: h.hostingManaged ?? '',
  }));
}

export function mapMulesoftOfferingToComponents(
  offering: MulesoftOffering,
  index: Map<string, MulesoftOffering> = new Map(),
): any[] {
  const componentsArray: any[] = [];
  const knowledge = offering.knowledgeContent ?? undefined;

  // Hero section
  componentsArray.push(
    stripNullish({
      _uid: uuidv4(),
      component: 'offering-hero',
      // The whole record, alongside the flattened fields the hero renders. The
      // factsheet button in the hero serialises the offering rather than
      // displaying it, so a projection would not do. Only this mapper supplies
      // it — the Storyblok path has no Process API record, which is what keeps
      // the button off that render.
      offering,
      displayName: offering.displayName,
      description: offering.description,
      offeringImage: offering.offeringImage,
      approvalStatus: offering.approvalStatus,
      offeringType: offering.offeringType,
      tags: offering.tags?.value,
      licenseCostResponsibility: offering.licenseCostResponsibility,
      successor: offering.successor
        ? resolveRef(offering.successor, index)
        : undefined,
      relationshipType: offering.offeringRelationship?.relationshipType,
      relationshipTarget: offering.offeringRelationship?.targetOfferingId
        ? resolveRef(offering.offeringRelationship.targetOfferingId, index)
        : undefined,
    }),
  );

  // Onboarding information
  const onboardingSteps = (offering.onboarding?.installationSteps ?? []).filter(
    (s) => s.description?.trim(),
  );
  if (onboardingSteps.length > 0) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-onboarding-steps',
        section:
          offering.offeringType === OfferingType.PROFESSIONAL_SERVICE
            ? 'Engagement information'
            : 'Onboarding information',
        onboardingAccessInstructions: offering.onboarding?.accessInstructions,
        onboardingSteps,
      }),
    );
  }

  // Services and Requests
  const servicesAndRequests = (offering.servicesAndRequests ?? [])
    .filter((s) => s.title?.trim() || s.description?.trim())
    .map((s) => ({
      id: String(offering.id ?? ''),
      type: s.type ?? '',
      title: s.title ?? '',
      description: s.description ?? '',
      destinationValue: s.destinationValue ?? '',
    }));
  if (servicesAndRequests.length > 0) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-services-and-requests',
        section: SERVICES_SECTION,
        servicesAndRequests,
      }),
    );
  }

  // Installations
  const validInstallations = (offering.installationInformation ?? []).filter(
    (i) => i.applicationType || i.instructions || i.url,
  );
  if (validInstallations.length > 0) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-installations',
        section: 'Installation & access information',
        installationInformation: validInstallations,
      }),
    );
  }

  // Details
  componentsArray.push(
    stripNullish({
      _uid: uuidv4(),
      component: 'offering-details',
      section: 'Offering details',
      approvalStatus: offering.approvalStatus,
      qualificationStatus: offering.qualificationStatus,
      licenseCostResponsibility: offering.licenseCostResponsibility,
      developmentCostResponsibility:
        offering.developmentCostResponsibility?.responsibility,
      developmentCostResponsibilityOtherDetails:
        offering.developmentCostResponsibility?.otherDetails,
      supportMaintenanceCostResponsibility:
        offering.supportMaintenanceCostResponsibility?.responsibility,
      supportMaintenanceCostResponsibilityOtherDetails:
        offering.supportMaintenanceCostResponsibility?.otherDetails,
      additionalCostConsiderations:
        offering.additionalCost?.additionalCostConsiderations,
      otherPricingModels: offering.otherPricingModels?.details,
      isPerUserPricingAvailable: offering.cost?.isPerUserPricingAvailable,
      perUserCostValue: offering.cost?.perUserCost?.perUserCostValue,
      perUserCostCurrency: offering.cost?.perUserCost?.perUserCostCurrency,
      perUserCostFrequency: offering.cost?.perUserCost?.perUserCostFrequency,
      systemRiskAssessmentName:
        offering.systemRiskAssessment?.systemRiskAssessmentName,
      systemRiskAssessmentUrl:
        offering.systemRiskAssessment?.systemRiskAssessmentUrl,
      classificationLevel: offering.classification?.classificationLevel,
      classificationRetentionPeriod:
        offering.classification?.classificationRetentionPeriod,
      hosting: mapHosting(offering),
      gxp: offering.gxP,
      singleSignOnIsSupported: offering.singleSignOn?.isSingleSignOnSupported,
      singleSignOnProviders: normalizeProviders(
        offering.singleSignOn?.singleSignOnProviders,
      ),
      isSupported: offering.isSupported,
      systemRiskAssessmentForSolutions: offering.isSraRequired,
      supportedDataClassificationLevel: offering.supportedDataClassification?.level,
      isExternalUseAllowed: offering.isExternalUseAllowed,
    }),
  );

  // Available versions
  const availableVersions = (offering.availableVersions ?? []).filter((v) =>
    v.version?.trim(),
  );
  if (availableVersions.length > 0) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-available-versions',
        section: 'Available versions',
        availableVersions,
      }),
    );
  }

  // Addon details
  const addonDetails = (offering.addonDetails ?? [])
    .filter((a) => a.addonName?.trim() || a.addonDescription?.trim())
    .map((a) => ({
      name: a.addonName ?? '',
      description: a.addonDescription ?? '',
      cost: a.addonCost ?? '',
      support: a.addonSupport ?? false,
      url: a.addonURL ?? '',
    }));
  if (addonDetails.length > 0) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-addons',
        section: 'Available add-ons',
        addonDetails,
      }),
    );
  }

  // Features
  const features = (offering.features ?? []).filter(
    (f) => f.name?.trim() || f.description?.trim(),
  );
  if (features.length > 0) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-features',
        section: 'Features',
        features,
      }),
    );
  }

  // Capabilities
  const capabilities = (offering.capabilities ?? []).filter(
    (c) => c.name?.trim() || c.description?.trim(),
  );
  if (capabilities.length > 0) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-capabilities',
        section: 'Major capabilities',
        capabilities,
      }),
    );
  }

  // Use cases
  const useCases = (offering.useCases ?? []).filter(
    (uc) => uc.name?.trim() || uc.description?.trim(),
  );
  if (useCases.length > 0) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-use-cases',
        section: 'Use cases',
        useCases,
      }),
    );
  }

  // Related technology
  const relatedTechnology = (offering.relatedTechnology ?? [])
    .filter((rt) => rt.targetId || rt.description?.trim())
    .map((rt) => ({
      targetId: rt.targetId ? resolveRef(rt.targetId, index) : rt.targetId,
      description: rt.description ?? '',
    }));
  if (relatedTechnology.length > 0) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-related-technology',
        section: 'Related technology',
        relatedTechnology,
      }),
    );
  }

  // Support channels
  const validChannels = (offering.supportChannels ?? []).filter(
    (sc) => sc.channelType || sc.destinationValue,
  );
  if (validChannels.length > 0) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-support-channels',
        section: 'Support options',
        supportChannels: validChannels,
      }),
    );
  }

  // Support hours
  const validHours = (offering.supportHours ?? []).filter(
    (sh) => (sh.days?.length ?? 0) > 0 || sh.startTime || sh.endTime,
  );
  if (validHours.length > 0) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-support-hours',
        section: 'Support hours',
        supportHours: validHours,
      }),
    );
  }

  // Environments
  const validEnvs = (offering.environment ?? [])
    .filter((env) => env.environmentName || env.environmentUrl)
    .map((env) => ({
      name: env.environmentName ?? '',
      url: env.environmentUrl ?? '',
      serviceLevelAgreement: env.serviceLevelAgreement ?? '',
    }));
  if (validEnvs.length > 0) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-environments',
        section: 'Environments & Service Level Agreement',
        environments: validEnvs,
      }),
    );
  }

  // Communities
  const validCommunities = (offering.community ?? []).filter(
    (c) => c.title || c.description || c.url,
  );
  if (validCommunities.length > 0) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-communities',
        section: 'Communities',
        communities: validCommunities,
      }),
    );
  }

  // Vendor
  const vendor = offering.vendorInfo;
  if (vendor?.vendorName || vendor?.vendorSupport || vendor?.vendorUrl) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-vendor',
        section: 'Vendor information',
        vendorName: vendor.vendorName,
        vendorSupport: vendor.vendorSupport,
        vendorUrl: vendor.vendorUrl,
      }),
    );
  }

  // Knowledge user instructions
  if (knowledge?.userInstructionsVisible) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'knowledge-user-instructions',
        knowledgeSection: 'User instructions',
        userInstructionLongDescription: knowledge.userInstructionLongDescription,
        userInstructions: knowledge.userInstructions ?? [],
      }),
    );
  }

  // Knowledge best practices
  if (knowledge?.bestPracticesVisible) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'knowledge-best-practices',
        knowledgeSection: 'Best practices',
        bestPractices: knowledge.bestPractices ?? [],
      }),
    );
  }

  // Knowledge learning resources
  if (knowledge?.learningResourcesVisible) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'knowledge-learning-resources',
        learningResources: knowledge.learningResources ?? [],
      }),
    );
  }

  // Knowledge technical resources
  if (knowledge?.technicalResourcesVisible) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'knowledge-technical-resources',
        knowledgeSection: 'Technical resources',
        technicalResources: knowledge.technicalResources ?? [],
      }),
    );
  }

  // Knowledge integrations
  if (knowledge?.integrationsVisible) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'knowledge-integrations',
        knowledgeSection: 'Integrations',
        integrationLongDescription: knowledge.integrationLongDescription,
        integrations: knowledge.integrations ?? [],
      }),
    );
  }

  // Knowledge faq
  if (knowledge?.faqVisible) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'knowledge-faq',
        knowledgeSection: 'FAQ',
        faq: knowledge.faq ?? [],
      }),
    );
  }

  // Knowledge additional resources
  if (knowledge?.additionalResourcesVisible) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'knowledge-additional-resources',
        knowledgeSection: 'Additional resources',
        additionalResources: knowledge.additionalResources ?? [],
      }),
    );
  }

  // Success stories
  if (knowledge?.successStoriesVisible) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'knowledge-success-stories',
        knowledgeSection: 'Success stories',
        successStories: knowledge.successStories ?? [],
      }),
    );
  }

  // Knowledge release notes
  if (knowledge?.releaseNotesVisible) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'knowledge-release-notes',
        knowledgeSection: 'Release notes',
        releaseNotes: knowledge.releaseNotes ?? [],
      }),
    );
  }

  // Similar offerings — keep only refs that resolve to a real offering; drop
  // free-text labels / unknown ids so the card grid never renders blanks.
  const similarOfferings = (offering.similarOfferings ?? [])
    .map((ref) => resolveRef(ref, index))
    .filter((r): r is ResolvedOfferingRef => r != null && typeof r === 'object');
  if (similarOfferings.length > 0) {
    componentsArray.push(
      stripNullish({
        _uid: uuidv4(),
        component: 'offering-related',
        extraSection: 'Explore similar offerings',
        similarOfferings,
      }),
    );
  }

  return componentsArray;
}
